import React from 'react';
import { CHAINS } from '../../config/constants';
import { formatNumber, formatCurrency, formatDuration, formatAddress } from '../../utils/format';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const TransactionSummary = ({ fromChain, toChain, amount, recipient, feeEstimate, timeDelay }) => {
  const fromChainInfo = Object.values(CHAINS).find(chain => chain.id === fromChain);
  const toChainInfo = Object.values(CHAINS).find(chain => chain.id === toChain);

  const receiveAmount = parseFloat(amount);

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-4">
      <h4 className="font-medium text-slate-900 dark:text-slate-100 text-center">
        Transaction Summary
      </h4>

      {/* Route Display */}
      <div className="flex items-center justify-between">
        <div className="flex-1 text-center">
          <div className="chain-badge chain-ronin mb-2" 
               style={{ backgroundColor: fromChainInfo?.color + '20', color: fromChainInfo?.color }}>
            {fromChainInfo?.name}
          </div>
          <div className="text-lg font-semibold">
            {formatNumber(amount, 6)} PFVS
          </div>
          <div className="text-sm text-slate-500">You send</div>
        </div>

        <div className="px-4">
          <ArrowRightIcon className="w-6 h-6 text-slate-400" />
        </div>

        <div className="flex-1 text-center">
          <div className="chain-badge chain-bnb mb-2"
               style={{ backgroundColor: toChainInfo?.color + '20', color: toChainInfo?.color }}>
            {toChainInfo?.name}
          </div>
          <div className="text-lg font-semibold">
            {formatNumber(receiveAmount, 6)} PFVS
          </div>
          <div className="text-sm text-slate-500">You receive</div>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="space-y-2 pt-3 border-t border-slate-200 dark:border-slate-700">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Exchange Rate:</span>
          <span className="font-medium">1:1</span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Network Fee:</span>
          <span className="font-medium">{formatCurrency(feeEstimate?.totalFeeUsd || 0)}</span>
        </div>

        {timeDelay && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">Processing Time:</span>
            <span className="font-medium">{formatDuration(timeDelay.delaySeconds)}</span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Route:</span>
          <span className="font-medium">
            {fromChain === CHAINS.RONIN.id ? 'Lock & Mint' : 'Burn & Release'}
          </span>
        </div>

        {recipient && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">Recipient:</span>
            <span className="font-medium font-mono">{formatAddress(recipient)}</span>
          </div>
        )}
      </div>

      {/* Security Information */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
        <div className="text-sm text-green-800 dark:text-green-200">
          <div className="font-medium mb-1">🔒 Security Features</div>
          <ul className="text-xs space-y-1 text-green-700 dark:text-green-300">
            <li>• Multi-relayer validation</li>
            <li>• Real-time monitoring and alerts</li>
            <li>• Automatic dispute resolution</li>
            {timeDelay && timeDelay.delaySeconds > 0 && (
              <li>• Security delay for large amounts</li>
            )}
          </ul>
        </div>
      </div>

      {/* Warnings */}
      {amount && parseFloat(amount) > 100000 && (
        <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
          <div className="text-sm text-warning-800 dark:text-warning-200">
            <div className="font-medium mb-1">⚠️ Large Amount Notice</div>
            <p className="text-xs text-warning-700 dark:text-warning-300">
              This transaction will be subject to enhanced security measures and may take longer to process.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionSummary; 